<script lang="ts">
	import {
		Button,
		ButtonType,
		ButtonSize,
		DetailedSelect,
		Textarea
	} from '@repo/commons/components'
	import type { Constraint } from '@semanticedge/persistence-service-noheaders-client/src/index.ts'
	import type {
		ConstraintSetIds,
		DetailedSelectItem,
		OutputObjectPrompts
	} from '@repo/commons/models'
	import {
		Modality,
		SupportedLanguage
	} from '@semanticedge/persistence-service-noheaders-client/src'
	import { t } from 'svelte-i18n'
	import { formatValidationErrorMessage, replaceNewlinesWithSpaces } from '@repo/commons/utils'

	const KEY_SEPARATOR = ', '
	const DEFAULT_CONSTRAINT_NAME = 'default'

	/** Returns a canonical key for a set of constraint ids (sorted), so [B,A] and [A,B] yield the same key. */
	function toCanonicalConstraintSetKey(ids: string[]): ConstraintSetIds {
		const sorted = [...ids].filter((id) => !!id).sort()
		return `[${sorted.join(KEY_SEPARATOR)}]` as ConstraintSetIds
	}

	/** Returns whether a key string represents the same set of constraint ids as the given canonical key. */
	function keyMatchesCanonical(promptsKey: string, canonicalKey: ConstraintSetIds): boolean {
		const idsStr = promptsKey.slice(1, -1)
		const ids = idsStr ? idsStr.split(/,\s*/).map((s) => s.trim()) : []
		return toCanonicalConstraintSetKey(ids) === canonicalKey
	}

	interface Props {
		constraints: Constraint[]
		onclose: () => void
		prompts?: OutputObjectPrompts
		selectedModality: Modality
		selectedLanguage: SupportedLanguage
		supportedLanguages?: SupportedLanguage[]
		onAddPrompt?: (payload: { constraintIds: string[]; promptText: string }) => void
	}

	let {
		constraints,
		onclose,
		prompts,
		selectedModality,
		selectedLanguage,
		supportedLanguages = [],
		onAddPrompt
	}: Props = $props()

	let formValue = $state<{ constraints: string[]; prompt: string }>({ constraints: [], prompt: '' })
	let errors = $state<{ constraints?: string; prompt?: string }>({})

	function findExistingKey(constraintIds: string[]): ConstraintSetIds | undefined {
		const canonicalKey = toCanonicalConstraintSetKey(constraintIds)
		return prompts
			? (Object.keys(prompts).find((k) => keyMatchesCanonical(k, canonicalKey)) as
					| ConstraintSetIds
					| undefined)
			: undefined
	}

	function getExistingModalityPrompts(constraintIds: string[]) {
		const existingKey = findExistingKey(constraintIds)
		return existingKey ? prompts![existingKey]?.[selectedModality] : undefined
	}

	const constraintOptions = $derived.by(
		() =>
			constraints.map((constraint) => {
				const isUsedDefault =
					constraint.name === DEFAULT_CONSTRAINT_NAME &&
					!!getExistingModalityPrompts([constraint.id!])
				return {
					title: constraint.name,
					id: constraint.id,
					disabled: isUsedDefault,
					description: isUsedDefault ? 'Default constraint can only be used once.' : undefined
				}
			}) as DetailedSelectItem[]
	)

	const selectedConstraintOptions = $derived.by(() =>
		constraintOptions.filter((option) => formValue.constraints.includes(option.id))
	)

	const allowLineBreaks = $derived(selectedModality !== Modality.Voice)

	function handleFieldChange(key: keyof typeof formValue, value: string | string[]) {
		formValue = { ...formValue, [key]: value }
		if (errors[key]) {
			errors = { ...errors, [key]: undefined }
		}
	}

	function validate(): boolean {
		const validationErrors: Record<string, string> = {}

		if (formValue.prompt.trim() === '') {
			const constraintsText = selectedConstraintOptions.map((option) => option.title).join(', ')
			const promptRequiredKey =
				selectedConstraintOptions.length === 1
					? 'OutputObjectForm.errors.promptRequired'
					: 'OutputObjectForm.errors.promptRequiredMultipleConstraints'
			validationErrors.prompt = formatValidationErrorMessage(promptRequiredKey, {
				language: selectedLanguage,
				modality: selectedModality,
				constraints: constraintsText
			})
		}

		const constraintIds: string[] = formValue.constraints.filter((item: string) => !!item)

		if (!constraintIds.length) {
			validationErrors.constraints = $t('OutputObjectForm.errors.atLeastOneConstraintRequired')
		}

		// Check if this set already exists in the selected modality (order-independent)
		const existingModalityPrompts = getExistingModalityPrompts(constraintIds)
		if (existingModalityPrompts) {
			// Now gotta check if some prompt for this constraint set already exists in the selected language.
			// If that's the case, the user should not be able to add this particular constraint set (but rather
			// adjust the prompt text of the existing one in the list or delete it).
			// A language added to the project later has no slot yet, but the set is still listed there with an empty prompt.
			if ((existingModalityPrompts[selectedLanguage] ?? ['']).length > 0) {
				/*
          Then there has to be a prompt defined for this constraint set in every language (even if it is empty).
          Otherwise we have a situation where the amount of constraint sets defined for one language is different
          than the other, and we assume that's not possible.
          */
				for (const lang of supportedLanguages) {
					if (!((existingModalityPrompts[lang] ?? ['']).length > 0)) {
						throw new Error(
							`Prompt for constraint set ${toCanonicalConstraintSetKey(constraintIds)} in language ${lang} does not exist,
                whereas it does exist in language ${selectedLanguage}. If a given constraint set has a prompt in one language,
                it should have a prompt in every language.`
						)
					}
				}

				// You already have a prompt for this constraint set in the selected language, so why don't you
				// go back and edit that prompt instead, rather than trying to add this constraint set again?
				validationErrors.constraints = $t('OutputObjectForm.errors.promptAlreadyExists')
			}
		}

		if (Object.keys(validationErrors).length > 0) {
			errors = validationErrors
			return false
		}

		return true
	}

	function resetForm() {
		formValue = { constraints: [], prompt: '' }
		errors = {}
	}

	function handleClose() {
		resetForm()
		onclose()
	}

	const handleAddPrompt = () => {
		if (!validate()) return
		const constraintIds = [...formValue.constraints].filter((id): id is string => !!id)
		onAddPrompt?.({ constraintIds, promptText: formValue.prompt.trim() })
		handleClose()
	}
</script>

<div class="constraint-prompt-form">
	<div class="modal-content">
		<DetailedSelect
			label="Constraint(s)"
			items={constraintOptions}
			selectedItems={selectedConstraintOptions}
			searchable={false}
			isRequired
			multiSelect
			onMultiSelect={(items) =>
				handleFieldChange(
					'constraints',
					items.map((item) => item.id)
				)}
			error={errors.constraints ? $t(errors.constraints) : undefined}
		/>
		<Textarea
			label="Prompt for constraint(s)"
			placeholder="Enter prompt for constraint(s)"
			value={formValue.prompt}
			onInput={(value: string) =>
				handleFieldChange('prompt', !allowLineBreaks ? replaceNewlinesWithSpaces(value) : value)}
			required
			rows={4}
			showError={!!errors.prompt}
			errorMessage={errors.prompt ? $t(errors.prompt) : undefined}
			{allowLineBreaks}
		/>
	</div>
	<div class="modal-footer">
		<Button
			buttonText="Add prompt"
			buttonType={ButtonType.BRAND}
			buttonSize={ButtonSize.LARGE}
			onclick={handleAddPrompt}
		/>
		<Button
			buttonText="Cancel"
			buttonType={ButtonType.TERTIARY}
			buttonSize={ButtonSize.LARGE}
			onclick={handleClose}
		/>
	</div>
</div>

<style lang="postcss">
	.constraint-prompt-form {
		@apply min-w-0 max-w-full w-[434px];
	}
	.modal-content {
		@apply flex flex-col gap-6;
	}
	.modal-footer {
		@apply flex items-center gap-4;
		@apply mt-8;
	}
</style>
